import React, { useState } from "react";
import {
    Box,
    Flex,
    Input,
    Menu, MenuButton, MenuList, MenuItem, Button, HStack, Text, Portal
} from "@chakra-ui/react";
import { FiSearch, FiChevronDown } from "react-icons/fi";
import { GenericSearchAndSortBarProps } from "../utils/types";

interface SortControlProps<T> {
    sortField: T;
    setSortField: (field: T) => void;
    sortOptions: { label: string; value: T }[];
}

const bg = "rgb(255 255 255)"
const border = "#eceef0";
const inputBg = "rgba(222, 222, 222, 0.14)";
const menuBg = "white";
const hoverBg = "#eff6ff";
const buttonBg = "rgba(222, 222, 222, 0.14)";

export const SortControl = <T,>({
    sortField,
    setSortField,
    sortOptions,
}: SortControlProps<T>) => {
    const currentOption = sortOptions.find((o) => o.value === sortField);

    return (
        <HStack spacing={2}>
            <Menu isLazy>
                <MenuButton
                    height="38px"
                    as={Button}
                    width="100%"
                    variant="outline"
                    size="sm"
                    px={4}
                    borderRadius="md"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    bg={buttonBg}
                    rightIcon={<FiChevronDown />}
                    border="2px solid"
                    borderColor={border}
                    _hover={{ borderColor: "blue.400", bg: hoverBg }}
                    fontSize="0.92rem"
                >

                    <Flex justify="space-between" align="center" width="100%" >
                        <Text color="gray.700" letterSpacing={-.20} fontSize="sm" >Sort By {currentOption?.label}</Text>
                    </Flex>
                </MenuButton>
                <Portal>
                    <MenuList
                        borderRadius="md"
                        shadow="lg"
                        bg={menuBg}
                        zIndex={9999}
                        position="absolute"
                        py={0}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem
                                key={String(option.value)}
                                onClick={() => setSortField(option.value)}
                                _hover={{ bg: "#eff6ff" }}
                                borderRadius="md"
                            >
                                <Flex justify="space-between" align="center" width="100%">
                                    <Text fontWeight={option.value === sortField ? 'medium' : 'normal'} fontSize={"xs"}>
                                        {option.label}
                                    </Text>
                                </Flex>
                            </MenuItem>
                        ))}
                    </MenuList>
                </Portal>
            </Menu>
        </HStack>
    );
};


export const GaraazInputSearchBar = <T,>({
    searchInput,
    onSearchChange,
    sortField,
    setSortField,
    sortOptions,
    placeholder = "Search...",
    showSortControl = false,
    extraComponent,
}: GenericSearchAndSortBarProps<T>) => {
    const [isInputFocused, setIsInputFocused] = useState(false);

    return (
        <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            justify="space-between"
            gap={4}
            px={4}
            py={4}
            bg={bg}
            backdropFilter="blur(16px)"
            border="1px solid"
            borderTop="1px solid"
            borderColor={border}
            transition="all 0.3s ease"
            wrap="wrap"
            boxShadow={"md"}
            fontFamily={"'Nunito','Inter', sans-serif"}
        >
            {/* 🔍 Search Field */}
            <Box flex={1} position="relative">
                <Input
                    type="text"
                    placeholder={placeholder}
                    value={searchInput}
                    onChange={onSearchChange}
                    size="sm"
                    pl={10}
                    fontSize="sm"
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    borderRadius="md"
                    border="2px solid"
                    borderColor={border}
                    height="38px"
                    bg={inputBg}
                    _hover={{ borderColor: "blue.400", bg: hoverBg }}
                    _focus={{
                        bg: inputBg,
                        boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.3)",
                    }}
                />
                <Box
                    position="absolute"
                    top="50%"
                    left="10px"
                    transform="translateY(-50%)"
                    color={isInputFocused ? "blue.500" : "gray.400"}
                    fontSize="md"
                    fontWeight={900}
                    zIndex={1}
                    pointerEvents="none"
                >
                    <FiSearch />
                </Box>
            </Box>

            {/* 🔽 Sort Control (Conditional) */}
            {showSortControl && (
                <Box width={{ base: "100%", md: "auto" }}>
                    <SortControl
                        sortField={sortField}
                        setSortField={setSortField}
                        sortOptions={sortOptions}
                    />
                </Box>
            )}

            {/* ➕ Extra Component Slot */}
            {extraComponent && (
                <Box width={{ base: "100%", md: "auto" }}>
                    {extraComponent}
                </Box>
            )}
        </Flex>
    );
};
